import { isIpadApp } from '@src/slide/infrastructure/module/dependent/config/tob/enterprise-config';
import { ALL } from './key-map';
import tdocsBridge from '@src/slide/infrastructure/module/independent/tdocsBridge/tdocsBridge';

const iPadKeyModifier = {
    NONE: '',
    COMMAND: 'command',
    CTRL: 'control',
    SHIFT: 'shift',
    ALT: 'alt',
};

interface IpadExternalKeyConfig {
    input: string,
    modifier?: string
}

// ipad 快捷键事件参数
interface IPadKeyboardEvent {
    code?: number;
    data?: IpadExternalKeyConfig;
    input?: string;
    modifier?: string;
    msg?: string;
}

const keyToModifierMap = {
    command: iPadKeyModifier.COMMAND,
    alt: iPadKeyModifier.ALT,
    shift: iPadKeyModifier.SHIFT,
    ctrl: iPadKeyModifier.CTRL,
    control: iPadKeyModifier.CTRL,
};

type ShortcutKeyEventCallback = (event: KeyboardEvent | null, ipadEvent: IPadKeyboardEvent) => void;

/**
 * ipad 外接键盘快捷键逻辑
 */
export class IpadExternalKeyboardEventManager {
    // 保存外界传入的事件回调和包装过的事件回调之间的映射，用于正确移除事件监听
    private static callbackMap = new WeakMap();
    // 保存已注册的快捷键列表
    private static registeredShortcutKeys = new Set();

    /**
     * 注册 ipad 外接键盘需要处理的快捷键
     * @param shortCutKey
     * @returns
     */
    public static registerShortcutKeyEvent(shortCutKey: string | string[]) {
        if (!this.isIpadExternalKeyboard()) {
            return;
        }
        // 过滤掉已经注册过的快捷键
        let shortCutKeys = Array.isArray(shortCutKey) ? shortCutKey : [shortCutKey];
        shortCutKeys = this.filterRegisteredShortcutKeys(shortCutKeys);
        const ipadExternalKeyConfigs = shortCutKeys.map(shortCutKeyToIpadExternalKeyConfig).filter(config => config);
        if (ipadExternalKeyConfigs.length) {
            tdocsBridge.invokeDocx('registerShortcutKeyEvent', {
                shortcutKey: ipadExternalKeyConfigs,
            });
        }
        // 更新已注册的快捷键列表
        this.updateRegisteredShortcutKeys(shortCutKeys);
    }

    /**
     * 添加快捷键事件回调
     * @param callback
     * @returns
     */
    public static addShortcutKeyEventListener(callback: ShortcutKeyEventCallback) {
        if (!this.isIpadExternalKeyboard()) {
            return;
        }
        if (!callback) {
            return;
        }
        const eventDecoratorForCallback = (ipadEvent: IPadKeyboardEvent) => {
            const keydownEvent = createKeyDownEventByIPadKeyboardEvent(ipadEvent);
            callback(keydownEvent, ipadEvent);
        };
        window.mqq?.addEventListener('shortcutKeyEvent', eventDecoratorForCallback);
        this.callbackMap.set(callback, eventDecoratorForCallback);
    }

    /**
     * 移除快捷键事件回调
     * @param callback
     * @returns
     */
    public static removeShortcutKeyEventListener(callback: ShortcutKeyEventCallback) {
        const eventDecoratorForCallback = this.callbackMap.get(callback);
        if (eventDecoratorForCallback) {
            window.mqq?.removeEventListener('shortcutKeyEvent', eventDecoratorForCallback);
        }
        this.callbackMap.delete(callback);
    }

    private static isIpadExternalKeyboard() {
        return window.mqq && isIpadApp();
    }

    private static filterRegisteredShortcutKeys(shortcutKeys: string[]) {
        return shortcutKeys.filter(shortcutKey => !this.registeredShortcutKeys.has(shortcutKey));
    }

    private static updateRegisteredShortcutKeys(shortcutKeys: string[]) {
        shortcutKeys.forEach(key => this.registeredShortcutKeys.add(key));
    }
}

// 注册 ipad 外接键盘快捷键时部分按键的 key 和正常键盘的 key 不一致，需要做转换
// 来源：https://doc.weixin.qq.com/doc/w3_m_dvOYJRVmGOfA?scode=AJEAIQdfAAo8kdGDG9ADcA1AYNACg
const keyboardToIpadExternalKeyboardMapping = {
    backspace: '\b',
    pagedown: 'UIKeyInputPageDown',
    pageup: 'UIKeyInputPageUp',
    home: 'UIKeyInputHome',
    end: 'UIKeyInputEnd',
};
const ipadExternalKeyboardToKeyboardMapping = {
    '\b': 'backspace',
    UIKeyInputPageDown: 'pagedown',
    UIKeyInputPageUp: 'pageup',
    UIKeyInputHome: 'home',
    UIKeyInputEnd: 'end',
};

function getIpadExternalKeyboardKey(keyboardKey: string) {
    return keyboardToIpadExternalKeyboardMapping[keyboardKey] || keyboardKey;
}

function getKeyboardKey(ipadExternalKeyboardKey: string) {
    return ipadExternalKeyboardToKeyboardMapping[ipadExternalKeyboardKey] || ipadExternalKeyboardKey;
}

/**
 * command+a 这种类型的快捷键转换为 ipad 外接键盘的快捷键配置
 * @param shortCutKey
 * @returns
 */
function shortCutKeyToIpadExternalKeyConfig(shortCutKey: string): IpadExternalKeyConfig | null {
    if (!shortCutKey) {
        return null;
    }
    const keys = shortCutKey.split('+');
    if (!keys?.length) {
        return null;
    }
    let input = '';
    const modifier: string[] = [];
    keys.forEach((key) => {
        const modifierKey = keyToModifierMap[key];
        if (!modifierKey) {
            if (!input) {
                input = getIpadExternalKeyboardKey(key);
            }
            return;
        }
        modifier.push(modifierKey);
    });
    if (input) {
        return {
            input,
            modifier: modifier.join('|'),
        };
    }
    return null;
}

/**
 * 根据 ipad 外接键盘快捷键事件模拟出正常 keydown 事件对象
 * @param event
 * @returns
 */
function createKeyDownEventByIPadKeyboardEvent(event: IPadKeyboardEvent): KeyboardEvent | null {
    const { input, modifier } = event;
    if (!input) {
        return null;
    }
    let ctrlKey = false;
    let altKey = false;
    let metaKey = false;
    let shiftKey = false;

    const { key, keyCode } = ALL[getKeyboardKey(input)];
    modifier?.split('|').forEach((modifierKey) => {
        if (modifierKey === iPadKeyModifier.COMMAND) {
            metaKey = true;
            return;
        }
        if (modifierKey === iPadKeyModifier.CTRL) {
            ctrlKey = true;
            return;
        }
        if (modifierKey === iPadKeyModifier.ALT) {
            altKey = true;
            return;
        }
        if (modifierKey === iPadKeyModifier.SHIFT) {
            shiftKey = true;
            return;
        }
    });
    return new KeyboardEvent('keydown', {
        altKey,
        metaKey,
        shiftKey,
        ctrlKey,
        key,
        keyCode,
        bubbles: true,
        cancelable: true,
        charCode: 0,
        code: key,
        detail: 0,
        repeat: false,
        which: keyCode,
    });
}

